import { useEffect, useState, useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Feature keys mapped to plans
const PLAN_FEATURES: Record<string, string[]> = {
  basic: [
    "dashboard", "staff", "children", "classes", "attendance",
    "parents", "billing", "salary_report", "subscription", "support", "settings",
    "observations",
  ],
  standard: [
    "dashboard", "staff", "children", "classes", "attendance",
    "parents", "billing", "salary_report", "subscription", "support", "settings",
    "observations",
    "lessons", "assignments", "live_sessions", "interactive_learning", "reports", "analytics",
    "activities",
  ],
  premium: [
    "dashboard", "staff", "children", "classes", "attendance",
    "parents", "billing", "salary_report", "subscription", "support", "settings",
    "observations",
    "lessons", "assignments", "live_sessions", "interactive_learning", "reports", "analytics",
    "activities", "game_reports",
  ],
};

// Always accessible regardless of subscription status
const ALWAYS_ALLOWED = ["dashboard", "subscription", "settings", "support"];

export interface SubscriptionInfo {
  plan: string | null;
  status: string | null;
  isActive: boolean;
  isExpired: boolean;
  isTrial: boolean;
  isSuspended: boolean;
  daysRemaining: number | null;
  loading: boolean;
  allowedFeatures: string[];
  canAccess: (featureKey: string) => boolean;
}

export function useSubscription(): SubscriptionInfo {
  const { profile, roles } = useAuth();
  const queryClient = useQueryClient();
  const nurseryId = profile?.nursery_id;

  // Super admins, parents, support, accountants bypass feature gating
  const isBypassed = roles.some(r =>
    ["super_admin", "parent", "support", "accountant", "call_center"].includes(r)
  );

  const { data: sub, isLoading } = useQuery({
    queryKey: ["nursery-subscription", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return null;
      const { data } = await supabase
        .from("nursery_subscriptions")
        .select("*")
        .eq("nursery_id", nurseryId)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();
      return data;
    },
    enabled: !!nurseryId && !isBypassed,
    staleTime: 30_000,
  });

  // Fetch module overrides for this nursery
  const { data: moduleOverrides } = useQuery({
    queryKey: ["nursery-modules", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return [];
      const { data } = await supabase
        .from("nursery_modules" as any)
        .select("module_key, is_enabled")
        .eq("nursery_id", nurseryId);
      return (data || []) as unknown as { module_key: string; is_enabled: boolean }[];
    },
    enabled: !!nurseryId && !isBypassed,
    staleTime: 30_000,
  });

  // Realtime subscription for live updates
  useEffect(() => {
    if (!nurseryId || isBypassed) return;

    const channel = supabase
      .channel(`sub-${nurseryId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "nursery_subscriptions",
          filter: `nursery_id=eq.${nurseryId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["nursery-subscription", nurseryId] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "nursery_modules",
          filter: `nursery_id=eq.${nurseryId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["nursery-modules", nurseryId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [nurseryId, isBypassed, queryClient]);

  const plan = sub?.plan ?? null;
  const status = sub?.status ?? null;
  const isTrial = sub?.is_trial ?? false;
  const trialEndsAt = (sub as any)?.trial_ends_at;

  const isExpired = useMemo(() => {
    if (!sub) return false;
    if (status === "expired" || status === "cancelled") return true;
    const endDate = isTrial ? trialEndsAt : sub.expires_at;
    if (endDate && new Date(endDate).getTime() < Date.now()) return true;
    return false;
  }, [sub, status, isTrial, trialEndsAt]);

  const isSuspended = status === "suspended";
  const isActive = status === "active" && !isExpired;

  const daysRemaining = useMemo(() => {
    if (!sub) return null;
    const endDate = isTrial ? trialEndsAt : sub.expires_at;
    if (!endDate) return null;
    const diff = new Date(endDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [sub, isTrial, trialEndsAt]);

  const allowedFeatures = useMemo(() => {
    if (isBypassed) return Object.values(PLAN_FEATURES).flat();
    if (!plan) return ALWAYS_ALLOWED;
    if (isExpired || isSuspended) return ALWAYS_ALLOWED;

    // Start with plan defaults
    let features = [...(PLAN_FEATURES[plan] || PLAN_FEATURES.basic)];

    // Apply module overrides
    if (moduleOverrides && moduleOverrides.length > 0) {
      for (const override of moduleOverrides) {
        if (override.is_enabled && !features.includes(override.module_key)) {
          features.push(override.module_key);
        } else if (!override.is_enabled) {
          features = features.filter(f => f !== override.module_key);
        }
      }
    }

    // Always keep ALWAYS_ALLOWED
    for (const key of ALWAYS_ALLOWED) {
      if (!features.includes(key)) features.push(key);
    }

    return features;
  }, [plan, isExpired, isSuspended, isBypassed, moduleOverrides]);

  const canAccess = useCallback(
    (featureKey: string) => {
      if (isBypassed) return true;
      return allowedFeatures.includes(featureKey);
    },
    [allowedFeatures, isBypassed]
  );

  return {
    plan,
    status,
    isActive,
    isExpired,
    isTrial,
    isSuspended,
    daysRemaining,
    loading: isLoading,
    allowedFeatures,
    canAccess,
  };
}
